import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Coins, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/points/")({
  head: () => ({ meta: [{ title: "J Cüzdanım — JUËN" }] }),
  component: PointsPage,
});

interface TxRow {
  id: string;
  amount: number;
  type: "earn" | "spend" | "purchase" | "admin_adjust";
  description: string | null;
  created_at: string;
}

interface PackageRow {
  id: string;
  name: string;
  description?: string | null;
  points: number;
  price_kurus: number;
  shopier_payment_url: string | null;
  is_active: boolean;
  sort_order: number;
}

function PointsPage() {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [txs, setTxs] = useState<TxRow[]>([]);
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  const load = async () => {
    if (!user) return;
    const [{ data: txData }, { data: pkgData }] = await Promise.all([
      supabase
        .from("point_transactions")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20),
      supabase
        .from("point_packages")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true }),
    ]);
    setTxs((txData as TxRow[]) ?? []);
    setPackages((pkgData as PackageRow[]) ?? []);
    setPageLoading(false);
  };

  useEffect(() => {
    if (user) void load();
  }, [user]);

  const handleBuy = (pkg: PackageRow) => {
    if (!user) return;
    if (!pkg.shopier_payment_url) {
      toast.error(t("account.points.noPaymentLink"));
      return;
    }
    void navigate({
      to: "/points/checkout/$orderId",
      params: { orderId: pkg.id },
    });
  };




  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  const balance = profile?.points_balance ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/dashboard" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("account.points.backToAccount")}
        </Link>

        <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/10 to-accent/10 p-6 shadow-card">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Coins className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-muted-foreground">{t("account.points.currentBalance")}</p>
              <p className="text-3xl font-bold">
                {balance.toLocaleString(locale)}{" "}
                <span className="text-base font-medium text-muted-foreground">J</span>
              </p>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{t("account.points.exchangeNote")}</p>
        </div>

        {/* Packages */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold">{t("account.points.packagesTitle")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("account.points.packagesDesc")}
          </p>

          {packages.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">{t("account.points.noPackages")}</p>
          ) : (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {packages.map((p) => (
                <div key={p.id} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-sm text-muted-foreground">{(p.price_kurus / 100).toLocaleString(locale)} ₺</div>
                  </div>
                  <div className="mt-1 text-2xl font-bold">
                    {p.points.toLocaleString(locale)} <span className="text-sm font-medium text-muted-foreground">J</span>
                  </div>
                  {p.description && <p className="mt-2 text-xs text-muted-foreground">{p.description}</p>}
                  <Button
                    type="button"
                    className="mt-3 w-full"
                    onClick={() => handleBuy(p)}
                    disabled={!p.shopier_payment_url}
                  >
                    {t("account.points.buy")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>


        {/* Terms */}
        <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div className="space-y-2 text-xs leading-relaxed text-foreground/90">
              <p className="font-semibold">{t("account.points.termsTitle")}</p>
              <ul className="list-disc space-y-1 pl-4">
                <li>
                  {t("account.points.term1Pre")}
                  <strong> {t("account.points.term1Strong")}</strong>
                </li>
                <li>{t("account.points.term2Pre")} <strong>{t("account.points.term2Strong")}</strong> {t("account.points.term2Post")}</li>
                <li>{t("account.points.term3Pre")} <strong>{t("account.points.term3Strong")}</strong>.</li>
                <li>{t("account.points.term4Pre")} <strong>{t("account.points.term4Strong")}</strong> {t("account.points.term4Post")}</li>
                <li>{t("account.points.term5Pre")} <strong>{t("account.points.term5Strong")}</strong>.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* History */}
        <h2 className="mt-10 text-xl font-semibold">{t("account.points.history")}</h2>
        <div className="mt-3 space-y-2">
          {txs.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("account.points.noHistory")}</p>
          ) : (
            txs.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{tx.description ?? labelForType(tx.type, t)}</div>
                  <div className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleString(locale)}</div>
                </div>
                <div className={`text-base font-semibold ${tx.amount >= 0 ? "text-success" : "text-destructive"}`}>
                  {tx.amount >= 0 ? "+" : ""}
                  {tx.amount} J
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function labelForType(type: TxRow["type"], t: (key: string) => string) {
  switch (type) {
    case "earn":
      return t("account.points.type.earn");
    case "spend":
      return t("account.points.type.spend");
    case "purchase":
      return t("account.points.type.purchase");
    case "admin_adjust":
      return t("account.points.type.adminAdjust");
  }
}
